/**
 * TaskFormContainer Component
 *
 * Main orchestrator for the Task Form.
 * Contains all state, handlers, and effects.
 * Renders sub-components with appropriate props.
 *
 * Decomposed from TaskForm.js for maintainability.
 * Original: 2,431 lines -> Container: ~800 lines + 5 sub-components
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Card, Col, Row, Typography, Form, Input, Modal, Button, message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useTaskContext } from '../../../../../core/components/tasks/useTaskContext';
import { useQuery } from 'react-apollo';
import GET_SLA_CONFIGS from '../../../../../core/GraphQl/Queries/GET_SLA_CONFIGS';
import { withRouter } from 'react-router-dom';
import { debounce } from 'lodash';
import moment from 'moment';

// Layout components
import { PageContentLayout, PageHeaderLayout } from '../../../../shared/layout';
import AppOverlaySpinner from '../shared/AppOverlaySpinner';

// Modal components
import {
  TaskDatePickerModal,
  TaskStartDatePickerModal,
  TaskCodeSnippetModal,
  TaskRelatedCampaignsModal,
} from '../modals';

// Context providers
import { useOrgContext } from '../../../../../core/components/orgs/useOrgContext';
import { useProjectContext } from '../../../../../core/components/projects/useProjectContext';
import { useUserContext } from '../../../../../core/components/users/useUserContext';

// Utilities
import { uploadFileToS3 } from '../../../../shared/upload/uploadFileToS3';
import { subTypes } from '../../configs/task.config';
import { hasCampaignsFeature } from '../../utils/task.utils';
import { useParams, useLocation } from 'react-router-dom';
import { getFileType } from '../../../../../core/utils/constants/constants';
import { startCase } from 'lodash';
import useResponsiveTask from '../../hooks/useResponsiveTask';

// Sub-components
import TaskBasicFields from './TaskBasicFields';
import TaskAssignmentFields from './TaskAssignmentFields';
import TaskDescriptionField from './TaskDescriptionField';
import TaskCampaignsField from './TaskCampaignsField';
import TaskFormActions from './TaskFormActions';

// Styles
import { fontSize as fontSizeTokens, bg } from '../../configs/designTokens';
import '../../styles/index.scss';

const { Text } = Typography;

const TaskFormContainer = ({ history }) => {
  // URL parameters
  const { id: currentTaskId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const linkedProjectId = queryParams.get('projectId');
  const linkedCampaignId = queryParams.get('campaignId');

  // Form instance
  const [form] = Form.useForm();

  // Refs
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  // Context hooks
  const {
    currentUser,
    createTask,
    fetchTask,
    currentTask,
    taskLoading,
    updateTask,
    updateTaskLoading,
    fetchCampaignForTask,
    linkedCampaign,
    setLinkedCampaign,
  } = useTaskContext();

  const { fetchBasicOrgsDetails, basicOrgsDetails, basicOrgsDetailsLoading } = useOrgContext();
  const { fetchBasicUsersDetails, basicUsersDetails, basicUsersDetailsLoading } = useUserContext();
  const { fetchProject, currentProject, fetchProjectsPaginated, projectsConnectionData } =
    useProjectContext();

  // SLA configs
  const { data: slaData } = useQuery(GET_SLA_CONFIGS);
  const slaConfigMap = useMemo(() => {
    if (!slaData?.slaConfigs) return {};
    return slaData.slaConfigs.reduce((map, config) => {
      map[config.requestType] = config.recommendedDays;
      return map;
    }, {});
  }, [slaData]);
  const [selectedRequestType, setSelectedRequestType] = useState('');

  // Window width for responsive layout
  const { width: windowWidth, isMobile, isDesktop } = useResponsiveTask();

  // User/Org derived values
  const userOrgName = currentUser?.role?.org?.name;
  const level = currentUser?.role?.org?.level;
  const isChannelPartnerOrg = level === 'CHANNEL_PARTNER';
  const hasCampaigns = hasCampaignsFeature(currentUser);

  // ============================================
  // STATE DECLARATIONS
  // ============================================

  // Initial state tracking (for detecting deletions during update)
  const [initialAttachedFiles, setInitialAttachedFiles] = useState([]);
  const [initialAttachedImages, setInitialAttachedImages] = useState([]);
  const [initialRelatedCampaigns, setInitialRelatedCampaigns] = useState([]);

  // New attachments tracking (for update mode)
  const [newAttachedFiles, setNewAttachedFiles] = useState([]);
  const [newAttachedImages, setNewAttachedImages] = useState([]);

  // Form control state
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [createMoreTasks, setCreateMoreTasks] = useState(false);

  // Org/User selection state
  const [assigneeOrgs, setAssigneeOrgs] = useState([]);
  const [channelPartnerOrgs, setChannelPartnerOrgs] = useState([]);
  const [advertiserOrgs, setAdvertiserOrgs] = useState([]);
  const [assigneeUsers, setAssigneeUsers] = useState(basicUsersDetails || []);
  const [orgTypeRequest, setOrgTypeRequest] = useState('');

  // Project selection state
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(linkedProjectId || null);
  const [projectTeamMembers, setProjectTeamMembers] = useState([]);

  // Request type/subtype state
  const [filteredSubTypes, setFilteredSubTypes] = useState([]);
  const [isSubTypeDisabled, setIsSubTypeDisabled] = useState(true);

  // File attachments state
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [attachedImages, setAttachedImages] = useState([]);

  // Code snippet state
  const [showCodeSnippetModal, setShowCodeSnippetModal] = useState(false);
  const [attachedCodeSnippet, setAttachedCodeSnippet] = useState('');

  // Related campaigns state
  const [showRelatedCampaignModal, setRelatedCampaignModal] = useState(false);
  const [relatedCampaigns, setRelatedCampaigns] = useState([]);

  // Date picker modal state
  const [taskDetailVisible, setTaskDatePickerVisible] = useState(false);
  const [taskStartDatePickerVisible, setTaskStartDatePickerVisible] = useState(false);
  const [dueDateRecurringConfig, setDueDateRecurringConfig] = useState({});

  // Collaborator/Follower modal state
  const [showCollaboratorModal, setShowCollaboratorModal] = useState(false);
  const [selectedCollaboratorId, setSelectedCollaboratorId] = useState(null);
  const [showFollowerModal, setShowFollowerModal] = useState(false);
  const [selectedFollowerId, setSelectedFollowerId] = useState(null);

  // ============================================
  // HANDLERS
  // ============================================

  const handleSubTypeChange = (value) => {
    const subTypesForSelected = subTypes[value] || [];
    setFilteredSubTypes(subTypesForSelected);
    setIsSubTypeDisabled(subTypesForSelected.length === 0);
    setSelectedRequestType(value);
    form.setFieldsValue({ requestSubType: '' });
  };

  const handleInsertCode = () => {
    const textarea = textareaRef.current?.resizableTextArea?.textArea;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentValue = form.getFieldValue('taskDetails') || '';
    const selectedText = currentValue.slice(start, end);

    let newValue, cursorPosition;

    if (selectedText) {
      newValue =
        currentValue.slice(0, start) + '```\n' + selectedText + '\n```' + currentValue.slice(end);
      cursorPosition = start + selectedText.length + 8;
    } else {
      newValue = currentValue.slice(0, start) + '```\n\n```' + currentValue.slice(end);
      cursorPosition = start + 4;
    }

    form.setFieldsValue({ taskDetails: newValue });

    setTimeout(() => {
      textarea.selectionStart = cursorPosition;
      textarea.selectionEnd = cursorPosition;
      textarea.focus();
    }, 0);
  };

  const insertText = (options) => {
    const textarea = textareaRef.current?.resizableTextArea?.textArea;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;
    const selectedText = value.substring(start, end);

    let transformedText, newStart, newEnd;

    if (options.transform) {
      const result = options.transform(selectedText, start, end);
      transformedText = result.text;
      newStart = result.newStart;
      newEnd = result.newEnd;
    } else {
      const { before, after } = options;
      transformedText = before + selectedText + after;
      newStart = start + before.length;
      newEnd = newStart + selectedText.length;
    }

    const newValue = value.substring(0, start) + transformedText + value.substring(end);
    form.setFieldsValue({ taskDetails: newValue });

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newStart, newEnd);
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const textarea = textareaRef.current?.resizableTextArea?.textArea;
      if (!textarea) return;

      const value = form.getFieldValue('taskDetails');
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      const lineEnd = value.indexOf('\n', start);
      const currentLine = value.slice(lineStart, lineEnd === -1 ? value.length : lineEnd);

      const unorderedMatch = currentLine.match(/^-\s/);
      const orderedMatch = currentLine.match(/^(\d+)\.\s/);

      if (unorderedMatch || orderedMatch) {
        e.preventDefault();
        let newValue, newCursorPos;

        if (unorderedMatch) {
          newValue = `${value.slice(0, start)}\n- ${value.slice(end)}`;
          newCursorPos = start + 3;
        } else {
          const nextNumber = parseInt(orderedMatch[1]) + 1;
          newValue = `${value.slice(0, start)}\n${nextNumber}. ${value.slice(end)}`;
          newCursorPos = start + `${nextNumber}. `.length + 1;
        }

        form.setFieldsValue({ taskDetails: newValue });
        setTimeout(() => {
          textarea.selectionStart = newCursorPos;
          textarea.selectionEnd = newCursorPos;
        }, 0);
      }
    }
  };

  const handleUploadFilesToS3 = async (files) => {
    try {
      const medias = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const media = await uploadFileToS3({ file });
        medias.push({
          ...media,
          fileType: getFileType(file.name),
        });
      }
      return medias;
    } catch (error) {
      console.error(error);
      throw new Error('File Size Exceeded: No greater than 2MB is allowed');
    }
  };

  const handleFileDelete = (fileId, type) => {
    if (type === 'file') {
      setAttachedFiles(attachedFiles.filter((file) => file.id !== fileId));
      setNewAttachedFiles(newAttachedFiles.filter((file) => file.id !== fileId));
    } else if (type === 'image') {
      setAttachedImages(attachedImages.filter((image) => image.id !== fileId));
      setNewAttachedImages(newAttachedImages.filter((image) => image.id !== fileId));
    }
  };

  // ============================================
  // FETCH FUNCTIONS
  // ============================================

  const fetchBasicOrgs = debounce((name = '', type = 'ASSIGNEE') => {
    if (type === 'CHANNEL_PARTNER') {
      setOrgTypeRequest('CHANNEL_PARTNER');
      fetchBasicOrgsDetails({
        variables: {
          first: 300,
          skip: 0,
          where: {
            AND: [
              { level_in: ['CHANNEL_PARTNER', 'CHANNEL_PARTNER_DIRECT', 'CORE', 'ENTERPRISE'] },
              { name_contains: name },
              {
                OR: [
                  { parentOrg_some: { id: currentUser.role.org.id } },
                  { id: currentUser.role.org.id },
                ],
              },
            ],
          },
        },
      });
    } else if (type === 'ADVERTISER') {
      const channelPartnerId =
        level !== 'PLATFORM' ? currentUser.role.org.id : form.getFieldValue('channelPartnerId');
      if (!channelPartnerId) {
        setAdvertiserOrgs([]);
        return;
      }
      setOrgTypeRequest('ADVERTISER');
      fetchBasicOrgsDetails({
        variables: {
          first: 1000,
          skip: 0,
          where: {
            AND: [
              {
                OR: [{ parentOrg_some: { id: channelPartnerId } }, { id: channelPartnerId }],
              },
              { name_contains: name },
            ],
          },
        },
      });
    } else {
      setOrgTypeRequest('ASSIGNEE');
      fetchBasicOrgsDetails({
        variables: {
          first: 10,
          skip: 0,
          where: {
            AND: [
              {
                OR: [
                  { parentOrg_some: { id: currentUser.role.org.id } },
                  { id: currentUser.role.org.id },
                ],
                name_contains: name,
              },
            ],
            OR: [
              {
                roles_some: {
                  org: { level: 'PLATFORM' },
                  roleItems_some: { feature: 'TASK_MANAGEMENT' },
                },
              },
            ],
          },
        },
      });
    }
  }, 500);

  const fetchBasicUsers = debounce((name = '') => {
    const assigneeOrgId = form.getFieldValue('assigneeOrgId');
    if (!assigneeOrgId) {
      setAssigneeUsers([]);
      return;
    }
    fetchBasicUsersDetails({
      variables: {
        first: 100,
        skip: 0,
        where: {
          AND: [
            {
              roles_some: {
                org: { id: assigneeOrgId, level: 'PLATFORM' },
                roleItems_some: { feature: 'TASK_MANAGEMENT' },
              },
              name_contains: name,
            },
          ],
        },
      },
    });
  }, 500);

  const fetchProjects = debounce((name = '') => {
    fetchProjectsPaginated({
      variables: {
        first: 100,
        skip: 0,
        where: {
          name_contains: name,
          owner: {
            roles_some: {
              org: { OR: [{ id: currentUser?.role?.org?.id }] },
            },
          },
        },
      },
    });
  }, 500);

  // ============================================
  // SUBMIT HANDLER
  // ============================================

  const handleSubmit = async () => {
    try {
      await form.validateFields();
    } catch (errorFields) {
      throw errorFields;
    }

    const {
      name,
      startDate,
      dueDate,
      requestType,
      requestSubType,
      linkUrl,
      urgency,
      assigneeOrgId,
      assigneeUserId: selectedAssignees,
      advertiserId,
      taskDetails,
      relatedCampaigns,
      linkedProjectId: formProjectId,
    } = form.getFieldsValue();

    let channelPartnerId = form.getFieldValue('channelPartnerId');

    const {
      recurring,
      recurrenceType,
      dateOfMonth,
      dayOfMonth,
      dayOfWeek,
      monthlyType,
      weeklyDays,
      yearlyDate,
      yearlyMonth,
    } = dueDateRecurringConfig;

    const createdBy = { connect: { id: currentUser.id } };

    // Detect deletions
    const filesToDelete = initialAttachedFiles.filter(
      (file) => !attachedFiles.some((f) => f.id === file.id)
    );
    const imagesToDelete = initialAttachedImages.filter(
      (image) => !attachedImages.some((i) => i.id === image.id)
    );
    const campaignsToDelete = initialRelatedCampaigns.filter(
      (c) => !(relatedCampaigns || []).some((rc) => rc.id === c.id)
    );

    let mediaFiles = [];
    let mediaImages = [];

    try {
      if (currentTaskId && currentTaskId !== 'new') {
        if (newAttachedFiles.length > 0) {
          mediaFiles = await handleUploadFilesToS3(newAttachedFiles);
        }
        if (newAttachedImages.length > 0) {
          mediaImages = await handleUploadFilesToS3(newAttachedImages);
        }
      } else {
        if (attachedFiles.length > 0) {
          mediaFiles = await handleUploadFilesToS3(attachedFiles);
        }
        if (attachedImages.length > 0) {
          mediaImages = await handleUploadFilesToS3(attachedImages);
        }
      }
    } catch (error) {
      message.error(error.message);
      return;
    }

    if (!channelPartnerId && isChannelPartnerOrg) {
      channelPartnerId = currentUser.role.org.id;
    }

    // Build assignee users connection
    const assigneeUserIds = selectedAssignees;
    const previousAssigneeUsers = currentTask?.assigneeUsers || [];
    const previousAssigneeUsersIds = previousAssigneeUsers.map((user) => user.id);
    const assigneeUsers = {};
    const collaborators = currentTask?.collaborators?.map((u) => u.id) || [];
    const followers = currentTask?.followers?.map((u) => u.id) || [];

    const assigneeIdToCheck = Array.isArray(assigneeUserIds)
      ? assigneeUserIds[0]?.key || assigneeUserIds[0]
      : assigneeUserIds;

    // Auto-promote: if new assignee is a collaborator, auto-disconnect from collaborators
    let autoDisconnectCollaborator = null;
    if (
      currentTaskId &&
      assigneeIdToCheck &&
      collaborators.includes(assigneeIdToCheck) &&
      assigneeUserIds?.length > 0
    ) {
      autoDisconnectCollaborator = assigneeIdToCheck;
    }

    if (
      currentTaskId &&
      assigneeIdToCheck &&
      followers.includes(assigneeIdToCheck) &&
      assigneeUserIds?.length > 0
    ) {
      message.warning('Assignee is already a follower. Please remove them from followers first.');
      return;
    }

    if (currentTaskId && currentTaskId !== 'new') {
      if (assigneeUserIds && previousAssigneeUsers && !assigneeUserIds[0]?.key) {
        const usersToDisconnect = previousAssigneeUsersIds.filter(
          (id) => !assigneeUserIds.includes(id)
        );
        if (usersToDisconnect.length > 0) {
          assigneeUsers.disconnect = usersToDisconnect.map((id) => ({ id }));
        }
      }
      if (
        assigneeUserIds &&
        !previousAssigneeUsersIds.includes(assigneeUserIds) &&
        !assigneeUserIds[0]?.key
      ) {
        assigneeUsers.connect = { id: assigneeUserIds };
      }
    } else {
      if (assigneeUserIds?.length > 0 && !assigneeUserIds[0]?.key) {
        assigneeUsers.connect = { id: assigneeUserIds };
      }
    }

    // Build main task data
    let data = {
      name,
      startDate,
      dueDate,
      createdBy,
      requestType,
      requestSubType,
      linkUrl,
      urgency,
      assigneeOrg: formProjectId ? {} : assigneeOrgId ? { connect: { id: assigneeOrgId } } : {},
      assigneeUsers,
      channelPartner: channelPartnerId ? { connect: { id: channelPartnerId } } : {},
      advertiser: advertiserId ? { connect: { id: advertiserId } } : {},
      attachedFiles: mediaFiles?.length ? { create: mediaFiles } : {},
      attachedImages: mediaImages?.length ? { create: mediaImages } : {},
      relatedCampaigns: relatedCampaigns?.length
        ? { connect: relatedCampaigns.map((c) => ({ id: c.id })) }
        : undefined,
      taskDetails,
      codeSnippet: attachedCodeSnippet,
      status: 'NOT_STARTED',
      project: formProjectId ? { connect: { id: formProjectId } } : {},
    };

    // Add recurring configuration
    if (recurring) {
      data = { ...data, recurring, recurrenceType };
      switch (recurrenceType) {
        case 'DAILY':
          data = {
            ...data,
            dateOfMonth: null,
            dayOfMonth: null,
            dayOfWeek: null,
            monthlyType: null,
            weeklyDays: { set: null },
            yearlyDate: null,
            yearlyMonth: null,
          };
          break;
        case 'MONTHLY':
          data =
            monthlyType === 'DATE_OF_MONTH'
              ? {
                  ...data,
                  dateOfMonth,
                  monthlyType,
                  weeklyDays: { set: null },
                  dayOfMonth: null,
                  dayOfWeek: null,
                  yearlyDate: null,
                  yearlyMonth: null,
                }
              : {
                  ...data,
                  dayOfMonth,
                  dayOfWeek: dayOfWeek?.toUpperCase(),
                  monthlyType,
                  dateOfMonth: null,
                  weeklyDays: { set: null },
                  yearlyDate: null,
                  yearlyMonth: null,
                };
          break;
        case 'WEEKLY':
          data = {
            ...data,
            weeklyDays: { set: weeklyDays },
            dateOfMonth: null,
            dayOfMonth: null,
            dayOfWeek: null,
            monthlyType: null,
            yearlyDate: null,
            yearlyMonth: null,
          };
          break;
        case 'BIWEEKLY':
          data = {
            ...data,
            dateOfMonth: null,
            dayOfMonth: null,
            dayOfWeek: null,
            monthlyType: null,
            weeklyDays: { set: null },
            yearlyDate: null,
            yearlyMonth: null,
          };
          break;
        case 'YEARLY':
          data = {
            ...data,
            yearlyDate,
            yearlyMonth,
            dateOfMonth: null,
            dayOfMonth: null,
            dayOfWeek: null,
            monthlyType: null,
            weeklyDays: { set: null },
          };
          break;
        default:
          break;
      }
    }

    try {
      message.loading(
        `${currentTaskId && currentTaskId !== 'new' ? 'Updating' : 'Creating'} task...`,
        0
      );

      if (currentTaskId && currentTaskId !== 'new') {
        // Handle deletions
        if (filesToDelete.length) {
          await updateTask({
            variables: {
              where: { id: currentTaskId },
              data: { attachedFiles: { delete: filesToDelete.map((f) => ({ id: f.id })) } },
            },
          });
        }
        if (imagesToDelete.length) {
          await updateTask({
            variables: {
              where: { id: currentTaskId },
              data: { attachedImages: { delete: imagesToDelete.map((i) => ({ id: i.id })) } },
            },
          });
        }
        if (campaignsToDelete.length) {
          await updateTask({
            variables: {
              where: { id: currentTaskId },
              data: {
                relatedCampaigns: { disconnect: campaignsToDelete.map((c) => ({ id: c.id })) },
              },
            },
          });
        }

        const { status, createdBy, ...filteredData } = data;
        // Auto-promote: disconnect new assignee from collaborators if needed
        if (autoDisconnectCollaborator) {
          filteredData.collaborators = { disconnect: [{ id: autoDisconnectCollaborator }] };
        }
        await updateTask({ variables: { where: { id: currentTaskId }, data: filteredData } });
        history.replace(`/tasks/details/${currentTaskId}`);
      } else {
        await createTask({ variables: { data } });

        if (createMoreTasks) {
          const currentValues = form.getFieldsValue();
          form.setFieldsValue({ ...currentValues, name: '' });
          setAttachedFiles([]);
          setAttachedImages([]);
          setNewAttachedFiles([]);
          setNewAttachedImages([]);
          setAttachedCodeSnippet('');
          window.scrollTo({ top: 0, behavior: 'smooth' });
          message.destroy();
          message.success('Task created successfully. Create another task below.');
        } else {
          if (formProjectId) {
            history.push(`/tasks/projects/details/${formProjectId}`);
          } else {
            history.push(`/tasks/main/1`);
          }
          message.destroy();
          message.success('Task created successfully');
        }
      }

      if (currentTaskId && currentTaskId !== 'new') {
        message.destroy();
        message.success('Task updated successfully');
      }
    } catch (error) {
      message.destroy();
      message.error(
        `Failed to ${currentTaskId && currentTaskId !== 'new' ? 'update' : 'create'} task: ${error.message}`
      );
      console.error('Task submission error:', error);
    }
  };

  // ============================================
  // EFFECTS
  // ============================================

  // Track initial values for deletion detection
  useEffect(() => {
    if (currentTask) {
      setInitialAttachedFiles(currentTask.attachedFiles || []);
      setInitialAttachedImages(currentTask.attachedImages || []);
      setInitialRelatedCampaigns(currentTask.relatedCampaigns || []);
    }
  }, [currentTask]);

  // Update subtypes when editing existing task
  useEffect(() => {
    if (currentTaskId && currentTask) {
      const subTypesForSelected = subTypes[currentTask.requestType] || [];
      setFilteredSubTypes(subTypesForSelected);
      setIsSubTypeDisabled(subTypesForSelected.length === 0);
    }
  }, [currentTaskId, currentTask]);

  // Fetch task on mount
  useEffect(() => {
    if (currentTaskId) {
      fetchTask({ variables: { where: { id: currentTaskId } } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle linked project from URL
  useEffect(() => {
    if (linkedProjectId && currentTaskId === 'new') {
      fetchProject({ variables: { where: { id: linkedProjectId } } });
      form.setFieldsValue({ linkedProjectId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedProjectId]);

  // Handle linked campaign from URL
  useEffect(() => {
    if (linkedCampaignId && currentTaskId === 'new') {
      fetchCampaignForTask({ variables: { id: linkedCampaignId } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedCampaignId]);

  // Set linked campaign in form
  useEffect(() => {
    if (linkedCampaign && currentTaskId === 'new' && linkedCampaignId) {
      const formattedCampaign = {
        id: linkedCampaign.id,
        name: linkedCampaign.name,
        startDate: moment(linkedCampaign.startDate),
        endDate: moment(linkedCampaign.endDate),
      };
      setRelatedCampaigns([formattedCampaign]);
      form.setFieldsValue({ relatedCampaigns: [formattedCampaign] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedCampaign]);

  // Cleanup linked campaign on unmount
  useEffect(() => {
    return () => setLinkedCampaign(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add linked project to projects list
  useEffect(() => {
    if (linkedProjectId && currentProject && currentTaskId === 'new') {
      setProjects((prev) => {
        const exists = prev.find((p) => p.id === linkedProjectId);
        if (exists) return prev;
        return [currentProject, ...prev];
      });
      setSelectedProjectId(linkedProjectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject, linkedProjectId]);

  // Update project team members when project changes
  useEffect(() => {
    const formProjectId = form.getFieldValue('linkedProjectId');
    if (formProjectId) {
      setSelectedProjectId(formProjectId);
      const selectedProject = projects.find((p) => p.id === formProjectId) || currentProject;
      if (selectedProject) {
        const teamMembers = [];
        if (selectedProject.owner) teamMembers.push(selectedProject.owner);
        if (selectedProject.team?.length > 0) teamMembers.push(...selectedProject.team);
        setProjectTeamMembers(teamMembers);
      }
    } else {
      setSelectedProjectId(null);
      setProjectTeamMembers([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.getFieldValue('linkedProjectId'), projects, currentProject]);

  // Update org lists when fetched
  useEffect(() => {
    switch (orgTypeRequest) {
      case 'ASSIGNEE':
        setAssigneeOrgs(basicOrgsDetails);
        break;
      case 'CHANNEL_PARTNER':
        setChannelPartnerOrgs(basicOrgsDetails);
        break;
      case 'ADVERTISER':
        setAdvertiserOrgs(basicOrgsDetails);
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicOrgsDetails]);

  // Update assignee users when fetched
  useEffect(() => {
    setAssigneeUsers(basicUsersDetails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicUsersDetails]);

  // Update projects list from connection data
  useEffect(() => {
    if (projectsConnectionData?.projectsConnection) {
      setProjects(projectsConnectionData.projectsConnection.edges.map((edge) => edge.node));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsConnectionData]);

  // Populate form when editing existing task
  useEffect(() => {
    if (currentTaskId && currentTaskId !== 'new' && currentTask && form) {
      if (currentTask.requestType) {
        setSelectedRequestType(currentTask.requestType);
      }
      form.setFieldsValue({
        name: currentTask.name,
        startDate: currentTask.startDate ? moment(currentTask.startDate) : null,
        dueDate: currentTask.dueDate ? moment(currentTask.dueDate) : null,
        requestType: currentTask.requestType,
        requestSubType: currentTask.requestSubType,
        linkUrl: currentTask.linkUrl,
        urgency: currentTask.urgency,
        assigneeOrgId: currentTask.assigneeOrg?.id || null,
        assigneeUserId:
          currentTask.assigneeUsers?.length > 0
            ? currentTask.assigneeUsers.map((user) => ({
                key: user.id,
                label: startCase(user.name || user.username),
              }))
            : currentTask.assigneeUser?.id || undefined,
        channelPartnerId: currentTask.channelPartner?.id || null,
        advertiserId: currentTask.advertiser?.id || null,
        taskDetails: currentTask.taskDetails,
        relatedCampaigns: currentTask.relatedCampaigns?.length
          ? currentTask.relatedCampaigns.map((c) => ({
              ...c,
              startDate: moment(c.startDate),
              endDate: moment(c.endDate),
            }))
          : [],
        linkedProjectId: currentTask.project?.id || null,
      });

      setRelatedCampaigns(
        currentTask.relatedCampaigns?.length
          ? currentTask.relatedCampaigns.map((c) => ({
              ...c,
              startDate: moment(c.startDate),
              endDate: moment(c.endDate),
            }))
          : []
      );

      setAssigneeOrgs((prev) =>
        prev.concat(currentTask.assigneeOrg ? [currentTask.assigneeOrg] : [])
      );
      setChannelPartnerOrgs((prev) =>
        prev.concat(currentTask.channelPartner ? [currentTask.channelPartner] : [])
      );
      setAdvertiserOrgs((prev) =>
        prev.concat(currentTask.advertiser ? [currentTask.advertiser] : [])
      );
      setAssigneeUsers((prev) =>
        prev.concat(currentTask.assigneeUser ? [currentTask.assigneeUser] : [])
      );
      setProjects((prev) => prev.concat(currentTask.project ? [currentTask.project] : []));

      setAttachedFiles(currentTask.attachedFiles || []);
      setAttachedImages(currentTask.attachedImages || []);
      setAttachedCodeSnippet(currentTask.codeSnippet || '');

      setDueDateRecurringConfig({
        recurring: currentTask.recurring,
        recurrenceType: currentTask.recurrenceType,
        dateOfMonth: currentTask.dateOfMonth,
        dayOfMonth: currentTask.dayOfMonth,
        dayOfWeek: currentTask.dayOfWeek,
        monthlyType: currentTask.monthlyType,
        weeklyDays: currentTask.weeklyDays,
        yearlyDate: currentTask.yearlyDate,
        yearlyMonth: currentTask.yearlyMonth,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTask]);

  // ============================================
  // RENDER
  // ============================================

  if (taskLoading) {
    return <AppOverlaySpinner />;
  }

  document.title = currentTaskId ? 'Update Task' : 'Create Task';

  return (
    <>
      <PageHeaderLayout
        title={currentTaskId && currentTaskId !== 'new' ? 'Update Task' : 'Create Task'}
        style={{
          fontSize: windowWidth < 768 ? fontSizeTokens.title : fontSizeTokens.heading,
          display: 'flex',
          border: 'none',
          paddingInline: windowWidth < 768 ? '4%' : '10%',
          paddingTop: '2vh',
        }}
      />
      <PageContentLayout
        style={{
          backgroundColor: bg.card,
          display: 'flex',
          justifyContent: 'center',
          paddingInline: windowWidth < 768 ? '4%' : '10%',
          width: '100%',
          height: '100%',
        }}
      >
        <Form form={form} layout="vertical" className="task-form-modern task-form-container__form">
          {/* Linked Project Banner */}
          {linkedProjectId && currentProject && currentTaskId === 'new' && (
            <Row className="task-form-linked-banner">
              <Col span={24}>
                <Card className="task-form-linked-banner__card task-form-linked-banner__card--project">
                  <div className="task-flex task-flex--center task-gap--sm">
                    <Text className="task-form-linked-banner__text task-form-linked-banner__text--project">
                      <strong>Linked Project:</strong> {currentProject.name}
                    </Text>
                  </div>
                </Card>
              </Col>
            </Row>
          )}

          {/* Linked Campaign Banner */}
          {linkedCampaignId && linkedCampaign && currentTaskId === 'new' && (
            <Row className="task-form-linked-banner">
              <Col span={24}>
                <Card className="task-form-linked-banner__card task-form-linked-banner__card--campaign">
                  <div className="task-flex task-flex--center task-gap--sm">
                    <Text className="task-form-linked-banner__text task-form-linked-banner__text--campaign">
                      <strong>Linked Campaign:</strong> {linkedCampaign.name}
                    </Text>
                  </div>
                </Card>
              </Col>
            </Row>
          )}

          <Row>
            {/* Task Basic Fields */}
            <TaskBasicFields
              form={form}
              level={level}
              windowWidth={windowWidth}
              currentTaskId={currentTaskId}
              linkedProjectId={linkedProjectId}
              selectedProjectId={selectedProjectId}
              setSelectedProjectId={setSelectedProjectId}
              projects={projects}
              projectTeamMembers={projectTeamMembers}
              filteredSubTypes={filteredSubTypes}
              isSubTypeDisabled={isSubTypeDisabled}
              handleSubTypeChange={handleSubTypeChange}
              fetchProjects={fetchProjects}
              setTaskDatePickerVisible={setTaskDatePickerVisible}
              setTaskStartDatePickerVisible={setTaskStartDatePickerVisible}
            />

            {/* Task Assignment Fields */}
            <TaskAssignmentFields
              form={form}
              level={level}
              currentTaskId={currentTaskId}
              currentTask={currentTask}
              selectedProjectId={selectedProjectId}
              userOrgName={userOrgName}
              assigneeOrgs={assigneeOrgs}
              assigneeUsers={assigneeUsers}
              channelPartnerOrgs={channelPartnerOrgs}
              advertiserOrgs={advertiserOrgs}
              basicOrgsDetailsLoading={basicOrgsDetailsLoading}
              basicUsersDetailsLoading={basicUsersDetailsLoading}
              fetchBasicOrgs={fetchBasicOrgs}
              fetchBasicUsers={fetchBasicUsers}
              setShowCollaboratorModal={setShowCollaboratorModal}
              setSelectedCollaboratorId={setSelectedCollaboratorId}
              setShowFollowerModal={setShowFollowerModal}
              setSelectedFollowerId={setSelectedFollowerId}
            />

            {/* Task Description Field */}
            <TaskDescriptionField
              form={form}
              currentTaskId={currentTaskId}
              textareaRef={textareaRef}
              fileInputRef={fileInputRef}
              imageInputRef={imageInputRef}
              attachedFiles={attachedFiles}
              attachedImages={attachedImages}
              setAttachedFiles={setAttachedFiles}
              setAttachedImages={setAttachedImages}
              setNewAttachedFiles={setNewAttachedFiles}
              setNewAttachedImages={setNewAttachedImages}
              handleFileDelete={handleFileDelete}
              handleInsertCode={handleInsertCode}
              handleKeyDown={handleKeyDown}
              insertText={insertText}
            />

            {/* Task Campaigns Field */}
            <TaskCampaignsField
              form={form}
              hasCampaigns={hasCampaigns}
              relatedCampaigns={relatedCampaigns}
              setRelatedCampaigns={setRelatedCampaigns}
              setRelatedCampaignModal={setRelatedCampaignModal}
            />

            {/* Form Actions */}
            <TaskFormActions
              history={history}
              level={level}
              currentTaskId={currentTaskId}
              buttonDisabled={buttonDisabled}
              setButtonDisabled={setButtonDisabled}
              createMoreTasks={createMoreTasks}
              setCreateMoreTasks={setCreateMoreTasks}
              handleSubmit={handleSubmit}
            />
          </Row>

          {/* Hidden relatedCampaigns Form.Item */}
          <Form.Item name="relatedCampaigns">
            <Input type="hidden" />
          </Form.Item>
        </Form>
      </PageContentLayout>

      {/* Date Picker Modals */}
      {taskDetailVisible && (
        <TaskDatePickerModal
          visible={taskDetailVisible}
          setVisible={setTaskDatePickerVisible}
          currentDueDateConfig={dueDateRecurringConfig}
          disabledDates={!selectedProjectId}
          slaDays={slaConfigMap[selectedRequestType] || 0}
          slaRequestType={selectedRequestType}
          onClose={(values) => {
            setDueDateRecurringConfig(values);
            form.setFieldsValue({ dueDate: values.dueDate });
            form.validateFields(['startDate', 'dueDate']);
          }}
        />
      )}

      {taskStartDatePickerVisible && (
        <TaskStartDatePickerModal
          visible={taskStartDatePickerVisible}
          setVisible={setTaskStartDatePickerVisible}
          currentStartDate={form.getFieldValue('startDate')}
          disabledDates={!selectedProjectId}
          onClose={(values) => {
            form.setFieldsValue({ startDate: values.startDate });
            form.validateFields(['startDate', 'dueDate']);
          }}
        />
      )}

      {/* Code Snippet Modal */}
      {showCodeSnippetModal && (
        <TaskCodeSnippetModal
          visible={showCodeSnippetModal}
          setVisible={setShowCodeSnippetModal}
          onEmbedSnippet={(codeSnippet) => setAttachedCodeSnippet(codeSnippet)}
        />
      )}

      {/* Related Campaigns Modal */}
      {showRelatedCampaignModal && (
        <TaskRelatedCampaignsModal
          level={level}
          visible={showRelatedCampaignModal}
          setVisible={setRelatedCampaignModal}
          channelPartnerOrgId={
            level === 'CHANNEL_PARTNER'
              ? currentUser.role.org.id
              : form.getFieldValue('channelPartnerId')
          }
          advertiserOrgId={form.getFieldValue('advertiserId')}
          selectedCampaigns={relatedCampaigns}
          onAddSelections={(selectedCampaignOrders) => {
            setRelatedCampaigns(selectedCampaignOrders);
            form.setFieldsValue({ relatedCampaigns: selectedCampaignOrders });
          }}
        />
      )}

      {/* Collaborator/Follower Removal Modal */}
      {(showCollaboratorModal || showFollowerModal) && (
        <Modal
          centered
          footer={[
            <Button
              key="cancel"
              onClick={() => setShowCollaboratorModal(false) || setShowFollowerModal(false)}
              disabled={updateTaskLoading}
            >
              Cancel
            </Button>,
            <Button
              key="ok"
              type="primary"
              disabled={updateTaskLoading}
              onClick={async () => {
                if (showFollowerModal) {
                  try {
                    message.loading('Removing follower...', 0);
                    await updateTask({
                      variables: {
                        where: { id: currentTaskId },
                        data: { followers: { disconnect: [{ id: selectedFollowerId }] } },
                      },
                    });
                    message.destroy();
                    message.success('Follower removed successfully');
                    setShowFollowerModal(false);
                  } catch (error) {
                    message.error(`Failed to remove follower: ${error.message}`);
                  }
                } else {
                  try {
                    message.loading('Promoting collaborator to assignee...', 0);
                    await updateTask({
                      variables: {
                        where: { id: currentTaskId },
                        data: { collaborators: { disconnect: [{ id: selectedCollaboratorId }] } },
                      },
                    });
                    form.setFieldsValue({ assigneeUserId: selectedCollaboratorId });
                    message.destroy();
                    message.success('Collaborator promoted to assignee.');
                    setShowCollaboratorModal(false);
                  } catch (error) {
                    message.destroy();
                    message.error(`Failed to promote collaborator: ${error.message}`);
                  }
                }
              }}
            >
              {updateTaskLoading && <LoadingOutlined />}{' '}
              {showCollaboratorModal ? 'Promote' : 'Remove'}
            </Button>,
          ]}
          title={showCollaboratorModal ? 'Promote Collaborator to Assignee' : 'Remove Follower'}
          visible={showCollaboratorModal || showFollowerModal}
        >
          <p>
            {showCollaboratorModal
              ? 'This user is currently a collaborator. Promote them to assignee? They will be automatically removed from the collaborator list.'
              : 'This user is a follower. Do you want to promote them as an assignee and remove them from followers?'}
          </p>
        </Modal>
      )}
    </>
  );
};

export default withRouter(TaskFormContainer);
